import { useMemo } from 'react'
import { Controller, useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button, Field, FieldDescription, FieldError, FieldLabel, Input, Switch } from '@clouway/ui'

import { UnsavedChangesGuard } from '@/components/UnsavedChangesGuard'
import type { UserInput } from '../types'

// Mirrors the backend's password format check (Argument.PASSWORD_PATTERN in
// accounts-idp, applied by SafeUserBase.registerUser). The pattern is
// compiled case-insensitively there, so its upper/lower lookaheads both
// collapse to "contains a letter" — the effective rule is 6-20 characters
// with at least one letter.
const PASSWORD_PATTERN = /^(?=.*[a-zA-Z]).{6,20}$/

const buildSchema = (
  passwordRequired: boolean,
  requiredMessage: string,
  invalidEmail: string,
  invalidPassword: string,
  passwordMismatch: string,
) =>
  z
    .object({
      email: z.string().min(1, requiredMessage).email(invalidEmail),
      firstName: z.string().min(1, requiredMessage),
      middleName: z.string(),
      familyName: z.string().min(1, requiredMessage),
      // Unconstrained for invite/edit — the field isn't rendered there, so it
      // must never block submission on its (blank) default value.
      password: passwordRequired ? z.string().regex(PASSWORD_PATTERN, invalidPassword) : z.string(),
      confirmPassword: z.string(),
      active: z.boolean(),
    })
    .refine((values) => !passwordRequired || values.password === values.confirmPassword, {
      message: passwordMismatch,
      path: ['confirmPassword'],
    })

// The full RHF value shape, including the confirm-password field that only
// exists to validate the typed password — it never leaves this component.
type UserFormSchema = z.infer<ReturnType<typeof buildSchema>>

// What callers (invite/register/update screens) receive: the wire-facing
// shape, with confirmPassword stripped out.
export type UserFormValues = Omit<UserFormSchema, 'confirmPassword'>

const emptyValues: UserFormSchema = {
  email: '',
  firstName: '',
  middleName: '',
  familyName: '',
  password: '',
  confirmPassword: '',
  active: true,
}

// Text fields shown in every mode; email is create-only-editable, password is
// register-only (the wire update request carries neither, and the invite
// request always sends it blank).
const textFields: { name: keyof UserFormValues; label: string }[] = [
  { name: 'firstName', label: 'user.form.firstName' },
  { name: 'middleName', label: 'user.form.middleName' },
  { name: 'familyName', label: 'user.form.familyName' },
]

// Strips the form-internal confirmPassword field before handing the values
// to the caller — downstream code only ever sees the wire-facing shape.
function toUserFormValues(values: UserFormSchema): UserFormValues {
  return {
    email: values.email,
    firstName: values.firstName,
    middleName: values.middleName,
    familyName: values.familyName,
    password: values.password,
    active: values.active,
  }
}

// invite: POST .../users with password=null, which triggers the IDP's
// set-password email — no password fields render at all. register: the
// admin types the password directly, for tenants whose user emails aren't
// real mailboxes. edit: profile fields only, no password (its own endpoints
// own status/removal).
export type UserFormMode = 'invite' | 'register' | 'edit'

const submitLabels: Record<UserFormMode, { idle: string; pending: string }> = {
  invite: { idle: 'user.form.invite', pending: 'user.form.inviting' },
  register: { idle: 'user.form.register', pending: 'user.form.registering' },
  edit: { idle: 'user.form.submit', pending: 'user.form.submitting' },
}

export function UserForm({
  mode,
  initialValues,
  onSubmit,
}: {
  mode: UserFormMode
  initialValues?: UserInput
  onSubmit: SubmitHandler<UserFormValues>
}) {
  const { t } = useTranslation('feature')
  const creating = mode !== 'edit'
  const passwordRequired = mode === 'register'
  const schema = useMemo(
    () =>
      buildSchema(
        passwordRequired,
        t('user.form.required'),
        t('user.form.invalidEmail'),
        t('user.form.invalidPassword'),
        t('user.form.passwordMismatch'),
      ),
    [passwordRequired, t],
  )
  const { control, handleSubmit, formState } = useForm<UserFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: initialValues ? { ...initialValues, confirmPassword: '' } : emptyValues,
  })

  const submit: SubmitHandler<UserFormSchema> = (values) => onSubmit(toUserFormValues(values))
  const submitLabel = submitLabels[mode]

  return (
    <form className="mx-auto flex w-full max-w-2xl flex-col gap-4" onSubmit={handleSubmit(submit)}>
      {/* isSubmitSuccessful only flips true AFTER onSubmit resolves, but these
          screens navigate away from INSIDE onSubmit — so without also
          checking isSubmitting the guard would still be armed (and intercept
          that navigation) at the moment it fires. RHF sets isSubmitting=true
          and re-renders before invoking onSubmit, so the guard is already
          disarmed by the time onSubmit's navigate() runs; a failed submit
          flips isSubmitting back to false and re-arms it. */}
      <UnsavedChangesGuard when={formState.isDirty && !formState.isSubmitting && !formState.isSubmitSuccessful} />

      <Controller
        control={control}
        name="email"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel htmlFor="user-email">{t('user.form.email')}</FieldLabel>
            <Input id="user-email" type="email" disabled={!creating} {...field} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      {textFields.map(({ name, label }) => (
        <Controller
          key={name}
          control={control}
          name={name}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel htmlFor={`user-${name}`}>{t(label as 'user.form.firstName')}</FieldLabel>
              <Input id={`user-${name}`} {...field} value={field.value as string} />
              {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
            </Field>
          )}
        />
      ))}

      {mode === 'invite' && (
        <Field>
          <FieldDescription>{t('user.form.inviteInfo')}</FieldDescription>
        </Field>
      )}

      {mode === 'register' && (
        <>
          <Controller
            control={control}
            name="password"
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel htmlFor="user-password">{t('user.form.password')}</FieldLabel>
                <Input id="user-password" type="password" {...field} />
                {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
              </Field>
            )}
          />
          <Controller
            control={control}
            name="confirmPassword"
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel htmlFor="user-confirm-password">{t('user.form.confirmPassword')}</FieldLabel>
                <Input id="user-confirm-password" type="password" {...field} />
                {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
              </Field>
            )}
          />
        </>
      )}

      {creating && (
        <Controller
          control={control}
          name="active"
          render={({ field }) => (
            <Field orientation="horizontal">
              <Switch id="user-active" checked={field.value} onCheckedChange={field.onChange} />
              <FieldLabel htmlFor="user-active">{t('user.form.active')}</FieldLabel>
            </Field>
          )}
        />
      )}

      <Button type="submit" disabled={formState.isSubmitting}>
        {t((formState.isSubmitting ? submitLabel.pending : submitLabel.idle) as 'user.form.invite')}
      </Button>
    </form>
  )
}
